import { Box, Chip, Grid, Stack, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { Assignment, AssignmentObject } from 'types/assignment';
import { TagData } from 'types/tag';

interface PreviewProps {
    assignment: Assignment;
}

interface TagListProps {
    tags: TagData[] | null | undefined;
}
const TagList = (props: TagListProps) => {
    const { tags } = props;
    if (!tags) {
        return null;
    }
    return (
        <Stack direction="row" spacing={1} ml={1}>
            {
                // eslint-disable-next-line react/destructuring-assignment
                tags.map(({ id, name }) => (
                    <Chip
                        key={id}
                        label={name}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ backgroundColor: (theme) => theme.palette.secondary.light }}
                    />
                ))
            }
        </Stack>
    );
};

const PreviewAssignment = (props: PreviewProps) => {
    const { assignment } = props;
    const intl = useIntl();

    const getGenericDisplayName = (object: AssignmentObject): string => {
        const typeTrans = intl.formatMessage({
            id: object.type
        });
        return object.type === 'container' ? `${typeTrans} ${object.container_id}` : `${typeTrans} ${object.unit_id}`;
    };

    const TitleLabel = ['unit', 'container'];

    return (
        <>
            <Box
                sx={{
                    width: '70vw',
                    height: 'auto',
                    maxWidth: '867px',
                    maxHeight: { xs: 'auto', md: 'auto' },
                    padding: '30px',
                    overflow: 'hidden'
                }}
            >
                <Typography sx={{ fontWeight: 400, fontSize: '16px', color: '#000000' }}>{assignment?.name}</Typography>
                <Grid container xs={12}>
                    <Grid item xs={12} sx={{ gap: '10px', padding: '30px 0 0 0' }} display={{ xs: 'block', md: 'flex' }}>
                        <Grid
                            item
                            xs={12}
                            md={6}
                            sx={{ backgroundColor: '#F7F4FB', borderRadius: '12px', padding: '10px 10px 10px 10px ' }}
                        >
                            <Typography
                                sx={{
                                    display: 'flex',
                                    fontWeight: 400,
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    color: '#7A49B9',
                                    marginBottom: '10px'
                                }}
                            >
                                <FormattedMessage id="assignment-details-label" />
                            </Typography>
                            <Typography
                                sx={{
                                    display: 'flex',
                                    marginBottom: '10px',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    lineHeight: '20px',
                                    color: '#878E95'
                                }}
                            >
                                <FormattedMessage id="name-show" />:{` ${assignment?.display_name}`}
                            </Typography>
                            <Typography
                                sx={{
                                    display: 'flex',
                                    marginBottom: '10px',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    lineHeight: '20px',
                                    color: '#878E95'
                                }}
                            >
                                <FormattedMessage id="category" />:{` ${assignment?.product_category?.name ?? '-'} `}
                            </Typography>
                            <Typography
                                sx={{
                                    display: 'flex',
                                    marginBottom: '10px',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    lineHeight: '20px',
                                    color: '#878E95',
                                    alignItems: 'center'
                                }}
                            >
                                <FormattedMessage id="tags" />:<TagList tags={assignment?.tags || []} />
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box>
                                <Typography
                                    sx={{
                                        display: 'flex',
                                        fontWeight: 400,
                                        fontSize: '16px',
                                        lineHeight: '24px',
                                        color: '#7A49B9',
                                        marginBottom: '10px'
                                    }}
                                >
                                    <FormattedMessage id="objects-assignment" />:
                                </Typography>
                            </Box>
                            <Box>
                                <table style={{ display: 'flex' }}>
                                    <tbody>
                                        {assignment?.objects?.map((item: AssignmentObject) => (
                                            <>
                                                {TitleLabel.includes(item?.type) && (
                                                    <tr style={{ display: 'flex', gap: '20px', width: '359px', marginBottom: '10px' }}>
                                                        <td
                                                            style={{
                                                                color: '#7A49B9',
                                                                fontWeight: 400,
                                                                fontSize: '14px',
                                                                lineHeight: '20px',
                                                                minWidth: '50%'
                                                            }}
                                                        >
                                                            {item.display_name !== '' ? item.display_name : getGenericDisplayName(item)}
                                                        </td>
                                                        <td
                                                            style={{
                                                                fontWeight: 400,
                                                                fontSize: '14px',
                                                                lineHeight: '20px',
                                                                color: '#878E95'
                                                            }}
                                                        >
                                                            <FormattedMessage id="object-type" />:{' '}
                                                            <FormattedMessage id={item?.container_type ?? item.type} />
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        ))}
                                    </tbody>
                                </table>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container xs={12}>
                    <Grid item xs={12} sx={{ gap: '10px', padding: '10px' }} display={{ xs: 'block', md: 'flex' }}>
                        <Grid item xs={12} md={6}>
                            <Box>
                                <Typography
                                    sx={{
                                        display: 'flex',
                                        fontWeight: 400,
                                        fontSize: '16px',
                                        lineHeight: '24px',
                                        color: '#7A49B9',
                                        marginBottom: '10px'
                                    }}
                                >
                                    <FormattedMessage id="products" />:
                                </Typography>
                            </Box>
                            <Box>
                                <table style={{ display: 'flex' }}>
                                    <tbody>
                                        {assignment?.products?.map((item: any) => (
                                            <>
                                                <tr style={{ display: 'flex', gap: '20px', width: '359px', marginBottom: '10px' }}>
                                                    <td
                                                        style={{
                                                            color: '#7A49B9',
                                                            fontWeight: 400,
                                                            fontSize: '14px',
                                                            lineHeight: '20px',
                                                            minWidth: '50%'
                                                        }}
                                                    >
                                                        {item.name}
                                                    </td>
                                                </tr>
                                            </>
                                        ))}
                                    </tbody>
                                </table>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default PreviewAssignment;
